import { z } from 'zod'
import { pageQuerySchema } from './common.js'
import type { Permission } from './permissions.js'

/**
 * Serial numbers (products-inventory §6.5, ASM-096). A product with `trackSerials` carries one serial
 * per unit; serials belong to the product (a tracked product has no variants) and there is no batch,
 * lot or expiry. Every stock document line of such a product lists its serials, and the line's
 * quantity must be a whole number equal to the number of serials when the document posts.
 *
 *   IN_STOCK              in one warehouse (received by a purchase, opening stock, a positive
 *                         adjustment or a customer return; moved by a transfer)
 *   SOLD                  issued by an invoice or a till sale
 *   RETURNED_TO_SUPPLIER  sent back by a supplier return (a later purchase may receive it again)
 *   REMOVED               taken out by a negative adjustment or count (may be received again)
 *   ISSUED_TO_PROJECT     issued to a project by a material issue (a material return brings it back)
 *
 * Voiding a document puts its serials back where they were, unless one has moved on since
 * (422 SERIAL_MOVED_ON).
 */
export const SERIAL_STATUSES = [
  'IN_STOCK',
  'SOLD',
  'RETURNED_TO_SUPPLIER',
  'REMOVED',
  'ISSUED_TO_PROJECT',
] as const
export const serialStatusSchema = z.enum(SERIAL_STATUSES)
export type SerialStatus = z.infer<typeof serialStatusSchema>

/** One serial number as typed or scanned: trimmed, 1–100 characters, compared case-insensitively. */
export const serialNumberSchema = z
  .string()
  .trim()
  .min(1, 'Enter the serial number')
  .max(100, 'A serial number has at most 100 characters')

/**
 * The `serials` of a document line: required (one per unit) for a serial-tracked product, refused
 * for any other line. Drafts may hold fewer than the quantity; posting needs exactly the quantity.
 */
export const lineSerialsSchema = z.array(serialNumberSchema).max(1000).default([])

/**
 * Stable error codes (422, with `errors[].path` naming the line, e.g. `lines.0.serials`, and the
 * offending numbers in `serials`).
 */
export const SERIAL_ERROR_CODES = [
  /** Serials on a line whose product does not track them. */
  'SERIALS_NOT_TRACKED',
  /** Posting: the number of serials differs from the quantity, or the quantity is not whole. */
  'SERIAL_COUNT_MISMATCH',
  /** A serial listed twice on the document, or received while it is already in stock or sold. */
  'SERIAL_DUPLICATE',
  /**
   * Issue / transfer / supplier return: not IN_STOCK at the document's warehouse. Customer return:
   * not sold on that invoice line (or already returned).
   */
  'SERIAL_NOT_AVAILABLE',
  /** Void: a serial of the document has moved on since (sold, returned, transferred…). */
  'SERIAL_MOVED_ON',
] as const
export type SerialErrorCode = (typeof SERIAL_ERROR_CODES)[number]

export interface SerialDocumentRef {
  /** SalesInvoice, CreditNote, Purchase, SupplierReturn, StockAdjustment, StockTransfer. */
  type: string
  id: string
  lineId: string | null
  /** The document number (null for a document without one). */
  number: string | null
}

/** A row of `GET /serials` (and `GET /products/:id/serials`). */
export interface ProductSerial {
  id: string
  serial: string
  status: SerialStatus
  product: { id: string; name: string; nameAr: string | null }
  variantId: string
  sku: string | null
  /** Where it is (IN_STOCK only). */
  warehouse: { id: string; code: string; name: string } | null
  /** The document that last brought it into stock from outside. */
  receivedBy: SerialDocumentRef | null
  /** The document that took it out (SOLD, RETURNED_TO_SUPPLIER, REMOVED, ISSUED_TO_PROJECT). */
  issuedBy: SerialDocumentRef | null
  createdAt: string
  updatedAt: string
}

export const SERIAL_SORT_FIELDS = ['serial', 'status', 'updatedAt', 'createdAt'] as const

/**
 * `GET /serials`: search and list serials (default sort `serial`). `q` matches the serial number
 * (substring, case-insensitive). `GET /products/:id/serials` is the same list for one product.
 */
export const serialListQuerySchema = pageQuerySchema.extend({
  productId: z.uuid().optional(),
  warehouseId: z.uuid().optional(),
  status: z.string().trim().toUpperCase().pipe(serialStatusSchema).optional(),
})
export type SerialListQuery = z.input<typeof serialListQuerySchema>

/**
 * `GET /serials/available`: the picker for issuing documents (invoice, till sale, supplier return,
 * transfer, negative adjustment): serials IN_STOCK at the warehouse, sorted by serial, at most
 * `limit`. Give the variant (as on the line) or the product.
 */
export const availableSerialsQuerySchema = z
  .object({
    variantId: z.uuid().optional(),
    productId: z.uuid().optional(),
    warehouseId: z.uuid(),
    q: z.string().trim().max(100).optional(),
    limit: z.coerce.number().int().min(1).max(1000).default(200),
  })
  .refine((v) => Boolean(v.variantId ?? v.productId), {
    path: ['variantId'],
    message: 'Give the variant or the product',
  })
export type AvailableSerialsQuery = z.input<typeof availableSerialsQuerySchema>

/**
 * `GET /serials/sold`: the picker for customer returns (credit note, till return): serials still
 * SOLD by that invoice (optionally one line of it). A serial already returned is not listed.
 */
export const soldSerialsQuerySchema = z.object({
  invoiceId: z.uuid(),
  invoiceLineId: z.uuid().optional(),
})
export type SoldSerialsQuery = z.input<typeof soldSerialsQuerySchema>

export interface SerialOption {
  id: string
  serial: string
  variantId: string
  /** `GET /serials/sold` only: the invoice line that sold it. */
  invoiceLineId?: string | null
}

export interface SerialMovement {
  id: string
  date: string
  reason:
    | 'OPENING'
    | 'PURCHASE'
    | 'PURCHASE_RETURN'
    | 'SALE'
    | 'SALE_RETURN'
    | 'TRANSFER'
    | 'ADJUSTMENT'
    | 'PROJECT_ISSUE'
    | 'PROJECT_RETURN'
  /** Null when this movement created the serial. */
  fromStatus: SerialStatus | null
  toStatus: SerialStatus
  fromWarehouse: { id: string; code: string } | null
  toWarehouse: { id: string; code: string } | null
  document: SerialDocumentRef
  /** True for the row a void appended; it undoes `reverses`. */
  reversal: boolean
  reverses: string | null
  createdAt: string
  createdBy: { id: string; name: string } | null
}

/** `GET /serials/:id`: the serial and its history, oldest first. */
export interface SerialDetail extends ProductSerial {
  movements: SerialMovement[]
}

/**
 * Who may use the serial pickers (`GET /serials/available`, `/serials/sold`): the people who create
 * the documents that take serials, plus anyone who may see stock.
 */
export const SERIAL_PICKER_PERMISSIONS = [
  'inventory.read',
  'invoices.create',
  'creditNotes.create',
  'purchases.create',
  'supplierReturns.create',
  'inventory.adjust',
  'inventory.transfer',
  'pos.sell',
  'pos.return',
] as const satisfies readonly [Permission, ...Permission[]]

/** Who may list serials and read a serial's history (with the Inventory module on: the API requires it). */
export const SERIAL_READ_PERMISSIONS = ['inventory.read', 'products.read'] as const satisfies readonly [
  Permission,
  ...Permission[],
]
